import { Component } from "react";
import { RotateCcw, ArrowLeft, Copy } from "lucide-react";
import { OrbitalMark } from "@/components/aph/OrbitalLock";

const describe = (e) => `${e?.name || "Error"}: ${e?.message || e}`.slice(0, 300);

export class ErrorBoundary extends Component {
  state = { error: null };
  recent = [];

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("Aphelion UI error", error, info?.componentStack);
    const detail = `${describe(error)}\n${(info?.componentStack || "").trim().split("\n").slice(0, 4).join("\n")}`;
    try { localStorage.setItem("aph_last_error", `${new Date().toISOString()} ${navigator.userAgent}\n${detail}`); } catch { /* storage full */ }
    const now = Date.now();
    this.recent = this.recent.filter((t) => now - t < 10000).concat(now);
    if (this.recent.length <= 2) setTimeout(this.recover, 0);
  }

  recover = () => {
    this.props.onRecover?.();
    this.setState({ error: null });
  };

  copy = () => navigator.clipboard?.writeText(localStorage.getItem("aph_last_error") || describe(this.state.error)).catch(() => {});

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="h-[100dvh] flex flex-col items-center justify-center gap-5 bg-void starfield text-ash px-8 text-center" data-testid="error-fallback">
        <OrbitalMark className="w-14 h-14" status="compromised" />
        <div>
          <h1 className="font-branding text-xl font-semibold">Orbit disrupted</h1>
          <p className="text-sm text-ash/50 mt-1 max-w-xs">Something went wrong on this screen. Your vault and messages are safe.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={this.recover} data-testid="error-recover-btn" className="flex items-center gap-2 bg-stellar text-void font-branding font-semibold rounded-secure px-4 py-2.5 text-sm"><ArrowLeft size={16} strokeWidth={1.5} /> Back to chats</button>
          <button onClick={() => window.location.reload()} data-testid="error-reload-btn" className="flex items-center gap-2 border border-rim rounded-secure px-4 py-2.5 text-sm hover:text-stellar"><RotateCcw size={16} strokeWidth={1.5} /> Reload</button>
        </div>
        <button onClick={this.copy} data-testid="error-details" className="max-w-xs font-mono text-[10px] text-ash/35 break-all flex items-start gap-1.5 text-left"><Copy size={12} strokeWidth={1.5} className="flex-shrink-0 mt-0.5" /> {describe(this.state.error)}</button>
      </div>
    );
  }
}
